// @ts-check
/**
 * SHIP-02/03: ship a reviewed coding result from the Changes panel. "Prepare
 * push" builds the exact record (remote, branch, the remote commit it may
 * replace, the local commit); "Push branch" asks once and pushes exactly that
 * with a lease. After a push, "Open draft PR" prefills the title and body from
 * the work item and the review.
 */
import { describeError } from "./errors.js";
import { pullRequestChip } from "./pull-request.js";
import { button, setBusy } from "./ui/button.js";
import { askToConfirm } from "./ui/dialog.js";
import { h } from "./ui/dom.js";
import { field } from "./ui/field.js";

const short = (/** @type {string | null | undefined} */ oid) =>
  oid ? oid.slice(0, 10) : "";

/** Words for a prepared push: what it will do on the remote. @param {any} push */
export function describePush(push) {
  const target = `${push.remoteName}/${push.branch}`;
  if (push.kind === "create")
    return `Creates ${target} at ${short(push.localCommit)}. The branch must not exist yet.`;
  if (push.kind === "fast_forward")
    return `Moves ${target} from ${short(push.expectedRemoteHead)} to ${short(push.localCommit)}.`;
  return `Replaces ${target} (now ${short(push.expectedRemoteHead)}) with ${short(push.localCommit)}, only if it hasn't moved.`;
}

/** @param {any} push */
export function pushStatusText(push) {
  if (push.status === "pushed")
    return `Pushed ${short(push.localCommit)} to ${push.remoteName}/${push.branch}.`;
  if (push.status === "pending") return `Ready to push. ${describePush(push)}`;
  if (push.status === "pushing") return "Pushing…";
  const words = describeError(push.error || "push_outcome_unknown");
  return `${words.title}. ${words.message}`;
}

/**
 * @param {{ client: any, uuid: () => string, tenantId: string, runId: string,
 *   workItemId?: string, publicationTarget?: { pullRequestId: string, remoteId: string, branch: string } | null,
 *   report: (error: unknown) => void }} options
 */
export function createShipPanel({
  client,
  uuid,
  tenantId,
  runId,
  workItemId,
  publicationTarget,
  report,
}) {
  const status = h("p", { class: "ship-status", role: "status" });
  const remote = /** @type {HTMLSelectElement} */ (
    h("select", { class: "ui-select ship-remote" })
  );
  const branch = /** @type {HTMLInputElement} */ (
    h("input", {
      class: "ui-input",
      placeholder: "agentspace/<work item>-<run id>",
      maxLength: 200,
      autocomplete: "off",
      spellcheck: false,
    })
  );
  const work = /** @type {HTMLSelectElement} */ (
    h("select", { class: "ui-select ship-work" })
  );
  const record = h("p", { class: "ship-record" });
  const pullHost = h("div", { class: "ship-pull" });
  /** @type {any} */
  let push;
  /** @type {any} */
  let pull;
  let prepareId = uuid();
  let busy = false;
  const prepare = button({
    text: "Prepare push",
    variant: "secondary",
    size: "sm",
    onClick: () => void act(prepare, prepareNow),
  });
  const pushButton = button({
    text: "Push branch",
    variant: "primary",
    size: "sm",
    onClick: () => void act(pushButton, pushNow),
  });
  const check = button({
    text: "Check the remote again",
    variant: "secondary",
    size: "sm",
    onClick: () => void act(check, checkNow),
  });
  const openPull = button({
    text: "Open draft PR",
    variant: "secondary",
    size: "sm",
    onClick: () => void act(openPull, draftNow),
  });
  const title = /** @type {HTMLInputElement} */ (
    h("input", { class: "ui-input", maxLength: 250 })
  );
  const body = /** @type {HTMLTextAreaElement} */ (
    h("textarea", { class: "ui-input ship-body", rows: 8, maxLength: 20000 })
  );
  const base = /** @type {HTMLInputElement} */ (
    h("input", { class: "ui-input", maxLength: 200, spellcheck: false })
  );
  const draft = /** @type {HTMLInputElement} */ (
    h("input", { type: "checkbox", checked: true })
  );
  let pullRequestId = uuid();
  const createPull = button({
    text: "Create draft pull request",
    variant: "primary",
    size: "sm",
    onClick: () => void act(createPull, createNow),
  });
  const pullForm = h(
    "div",
    { class: "ship-pull-form", hidden: true },
    field({ label: "Title", control: title }),
    field({ label: "Description", control: body }),
    field({ label: "Base branch", control: base }),
    h("label", { class: "ship-draft" }, draft, " Open as a draft"),
    createPull,
  );
  const element = h(
    "section",
    { class: "ship-panel", aria: { label: "Ship" } },
    h("h3", { text: "Ship" }),
    h("p", {
      class: "muted",
      text: publicationTarget
        ? "Push this reviewed fix to the existing pull request. Confirm the exact commit and branch before pushing."
        : "Push the reviewed result to a tested remote, then open a draft pull request. Nothing is pushed until you confirm the exact commit and branch.",
    }),
    field({ label: "Remote", control: remote }),
    field({
      label: "Branch",
      control: branch,
      hint: "Leave empty for the default name.",
      optional: true,
    }),
    field({ label: "Work item", control: work, optional: true }),
    h("div", { class: "ship-actions" }, prepare, pushButton, check, openPull),
    record,
    status,
    pullForm,
    pullHost,
  );
  /** @param {string} text @param {boolean} [failed] */
  const say = (text, failed = false) => {
    status.textContent = text;
    status.dataset.tone = failed ? "error" : "info";
  };
  /** @param {HTMLButtonElement} control @param {() => Promise<void>} work */
  async function act(control, work) {
    if (busy) return;
    busy = true;
    setBusy(control, true);
    try {
      await work();
    } catch (error) {
      say(
        /** @type {Error} */ (error)?.message || "Something went wrong.",
        true,
      );
      report(error);
    } finally {
      busy = false;
      setBusy(control, false);
      sync();
    }
  }
  function sync() {
    const pending = push?.status === "pending";
    pushButton.hidden = !pending;
    check.hidden = push?.status !== "unknown";
    openPull.hidden = !(
      push?.status === "pushed" &&
      !pull &&
      !publicationTarget
    );
    record.textContent = push
      ? `${describePush(push)} ${push.changedFiles} file${push.changedFiles === 1 ? "" : "s"}.`
      : "";
    pullHost.replaceChildren(...(pull ? [pullRequestChip(pull)] : []));
    for (const control of [remote, branch, work])
      control.disabled = Boolean(
        (control === work && workItemId) ||
          (publicationTarget && (control === remote || control === branch)) ||
          (push && push.status !== "rejected"),
      );
  }
  async function prepareNow() {
    if (!remote.value) throw new Error("Choose a tested remote first.");
    say("Building the commit and reading the remote branch…");
    push = await client.prepareBranchPush(
      { tenantId },
      {
        requestId: prepareId,
        runId,
        remoteId: publicationTarget?.remoteId ?? remote.value,
        ...(publicationTarget || branch.value.trim()
          ? { branch: publicationTarget?.branch ?? branch.value.trim() }
          : {}),
        ...(workItemId || work.value
          ? { workItemId: workItemId || work.value }
          : {}),
      },
    );
    say(pushStatusText(push));
  }
  async function pushNow() {
    if (!push) return;
    if (
      !(await askToConfirm(
        `Push branch? ${describePush(push)} Hooks never run and nothing else on the remote changes.`,
      ))
    )
      return;
    say("Pushing…");
    push = await client.startBranchPush(
      { tenantId, pushId: push.id },
      {
        remoteId: push.remoteId,
        branch: push.branch,
        expectedRemoteHead: push.expectedRemoteHead,
        localCommit: push.localCommit,
      },
    );
    if (push.status === "rejected") prepareId = uuid();
    say(pushStatusText(push), push.status !== "pushed");
  }
  async function checkNow() {
    if (!push) return;
    push = await client.getBranchPush({ tenantId, pushId: push.id });
    say(
      pushStatusText(push),
      push.status !== "pushed" && push.status !== "pending",
    );
  }
  async function draftNow() {
    if (!push) return;
    const value = await client.pullRequestDraft({ tenantId, pushId: push.id });
    if (!value.supported) {
      say(describeError("forge_unsupported").message, true);
      return;
    }
    title.value = value.title;
    body.value = value.body;
    base.value = value.baseBranch;
    pullForm.hidden = false;
    title.focus();
  }
  async function createNow() {
    if (!push) return;
    if (!title.value.trim()) throw new Error("Add a title first.");
    say("Opening the pull request…");
    const created = await client.createPullRequest(
      { tenantId },
      {
        requestId: pullRequestId,
        pushId: push.id,
        title: title.value.trim(),
        body: body.value,
        ...(base.value.trim() ? { baseBranch: base.value.trim() } : {}),
        draft: draft.checked,
      },
    );
    pull = created;
    if (created.state === "open" || created.state === "merged") {
      pullForm.hidden = true;
      say(
        `Opened ${created.draft ? "draft " : ""}pull request #${created.number}.`,
      );
    } else {
      say(
        created.state === "unknown"
          ? `${describeError("forge_outcome_unknown").title}. ${describeError("forge_outcome_unknown").message}`
          : describeError(created.error || "forge_unavailable").message,
        true,
      );
      if (created.state === "creating") pullRequestId = uuid();
    }
  }
  /** Load remotes, open work items and this run's latest push and pull request. */
  async function load() {
    const [repositories, items, pushes, pulls] = await Promise.all([
      client.listRepositories({ tenantId }),
      workItemId
        ? Promise.resolve({
            items: [{ id: workItemId, title: "This conversation's work" }],
          })
        : client
            .listWork(
              { tenantId },
              { status: "backlog,ready,in_progress,review" },
            )
            .catch(() => ({ items: [] })),
      client.listBranchPushes({ tenantId }, { runId, limit: 5 }),
      client.listPullRequests({ tenantId }, { runId, limit: 5 }),
    ]);
    const remotes = (
      await Promise.all(
        repositories.map(async (/** @type {any} */ repository) =>
          (
            await client.listRepositoryRemotes(
              { tenantId, repositoryId: repository.id },
              { limit: 20 },
            )
          ).items.map((/** @type {any} */ item) => ({ ...item, repository })),
        ),
      )
    ).flat();
    remote.replaceChildren(
      h("option", {
        value: "",
        text: remotes.length ? "Choose a remote" : "No remotes yet",
      }),
      ...remotes.map((/** @type {any} */ item) =>
        h("option", {
          value: item.id,
          disabled: item.probe.status !== "connected",
          text: `${item.repository.name} · ${item.name}${item.probe.status === "connected" ? "" : " (test it first)"}`,
        }),
      ),
    );
    work.replaceChildren(
      h("option", { value: "", text: "None" }),
      ...items.items.map((/** @type {any} */ item) =>
        h("option", { value: item.id, text: item.title }),
      ),
    );
    if (workItemId) {
      work.value = workItemId;
      work.disabled = true;
    }
    push = pushes.items[0];
    pull = pulls.items[0];
    if (publicationTarget) {
      remote.value = publicationTarget.remoteId;
      branch.value = publicationTarget.branch;
    }
    if (push) {
      remote.value = push.remoteId;
      branch.value = push.branch;
      if (push.workItemId) work.value = push.workItemId;
      say(pushStatusText(push), ["rejected", "unknown"].includes(push.status));
      if (push.status === "rejected") push = undefined;
    } else if (!remotes.length)
      say(
        "Add a remote on Connections › Repositories, test it, then come back.",
      );
    sync();
  }
  sync();
  return {
    element,
    load: () =>
      load().catch((error) => {
        say(
          /** @type {Error} */ (error)?.message || "Could not load remotes.",
          true,
        );
      }),
  };
}
